/* =============================================================
   news-index.js — BMW-styled Insider News interactions
   • Scroll-driven progress bar
   • IntersectionObserver reveal (.ni-reveal)
   • Pretext-inspired Canvas excerpt height measurement
   • Staggered hero entrance
   ============================================================= */
(function () {
  'use strict';

  /* ── Canvas-based text height measurement (Pretext pattern) ──
     Measures how many lines a string needs at a given width/font,
     then clamps excerpt elements to exactly N lines. This avoids
     layout-jump from max-height:9999px and doesn't need DOM reflow.
  ----------------------------------------------------------------- */
  var _ptxCache = new Map();

  function ptxMeasure(text, fontStr, containerWidth, lineHeightPx) {
    var cacheKey = fontStr + '|' + containerWidth + '|' + text.length;
    if (_ptxCache.has(cacheKey)) return _ptxCache.get(cacheKey);

    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    ctx.font = fontStr;

    var words = text.split(' ');
    var lines = 0;
    var current = '';
    for (var i = 0; i < words.length; i++) {
      var test = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > containerWidth && current) {
        lines++;
        current = words[i];
      } else {
        current = test;
      }
    }
    if (current) lines++;

    var result = { lineCount: Math.max(1, lines), height: Math.max(1, lines) * lineHeightPx };
    _ptxCache.set(cacheKey, result);
    return result;
  }

  /* Prefer window.__bmwPretext if the global bundle is loaded */
  function measureHeight(text, fontStr, containerWidth, lineHeightPx) {
    if (window.__bmwPretext && typeof window.__bmwPretext.measureBlockHeightCached === 'function') {
      return window.__bmwPretext.measureBlockHeightCached(text, fontStr, containerWidth, lineHeightPx);
    }
    return ptxMeasure(text, fontStr, containerWidth, lineHeightPx).height;
  }

  /* ── Clamp excerpt elements to 3 visible lines ── */
  function clampExcerpts() {
    var excerpts = document.querySelectorAll('.ni__card-excerpt[data-full-text]');
    if (!excerpts.length) return;

    var lineHeight = 24; /* 15px × 1.65 ≈ 24px; keep in sync with CSS */
    var maxLines   = 3;
    var fontStr    = '300 15px Inter, system-ui, sans-serif';

    excerpts.forEach(function (el) {
      var fullText = el.getAttribute('data-full-text');
      var containerWidth = el.getBoundingClientRect().width || el.offsetWidth;
      if (!containerWidth) return;

      var h = measureHeight(fullText, fontStr, containerWidth, lineHeight);
      var maxH = maxLines * lineHeight;

      if (h > maxH) {
        el.style.maxHeight = maxH + 'px';
        el.style.overflow = 'hidden';
        el.style.position = 'relative';
        /* Gradient fade-out mask */
        el.style.webkitMaskImage = 'linear-gradient(to bottom, black 60%, transparent 100%)';
        el.style.maskImage       = 'linear-gradient(to bottom, black 60%, transparent 100%)';
      }
    });
  }

  /* ── Progress bar ── */
  function initProgressBar() {
    var bar = document.querySelector('.ni__progress-bar');
    if (!bar) return;
    function update() {
      var doc = document.documentElement;
      var pct = (doc.scrollTop || document.body.scrollTop) /
                ((doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight);
      bar.style.width = Math.min(100, pct * 100).toFixed(2) + '%';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    var targets = document.querySelectorAll('.ni-reveal');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('visible'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ── Hero stagger (runs once on load) ── */
  function initHeroStagger() {
    var staggerEls = document.querySelectorAll('.ni__hero [data-stagger]');
    if (!staggerEls.length) return;
    staggerEls.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(14px)';
      el.style.transition = 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)';
      el.style.transitionDelay = (i * 0.1 + 0.1) + 's';
      setTimeout(function () {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }, 80);
    });
  }

  /* ── Article card hover accent line ── */
  function initCardHover() {
    var cards = document.querySelectorAll('.ni__card');
    cards.forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        card.style.borderTopColor = 'var(--ni-primary)';
        card.style.borderTopWidth = '2px';
        card.style.paddingTop = 'calc(32px - 1px)';
      });
      card.addEventListener('mouseleave', function () {
        card.style.borderTopColor = '';
        card.style.borderTopWidth = '';
        card.style.paddingTop = '';
      });
    });
  }

  /* ── Main init ── */
  function init() {
    initProgressBar();
    initReveal();
    initHeroStagger();
    clampExcerpts();
    initCardHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Support Shopify section reload */
  document.addEventListener('shopify:section:load', init);

  /* Re-clamp on resize */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(clampExcerpts, 200);
  }, { passive: true });

})();
